import type {
  FontSystemIssue,
  FontSystemValidationResult,
} from "./types"

const groupBy = <T>(items: readonly T[], key: (item: T) => string): Map<string, T[]> => {
  const map = new Map<string, T[]>()
  for (const item of items) {
    const group = key(item)
    const existing = map.get(group)
    if (existing) existing.push(item)
    else map.set(group, [item])
  }
  return map
}

const formatIssue = (issue: FontSystemIssue): string => {
  switch (issue._tag) {
    case "MissingFontManifest":
    case "MissingAstroConfig":
    case "MissingLayoutAstro":
    case "MissingGlobalCss":
      return issue.path
    case "InvalidFontManifestJson":
      return `${issue.path}: ${issue.message}`
    case "InvalidFontManifestSchema":
      return `${issue.path}: ${issue.field} ${issue.message}`
    case "AstroConfigMissingFontProvidersImport":
      return 'Missing `fontProviders` import from "astro/config".'
    case "AstroConfigMissingManifestImport":
      return "Missing import of src/theme/font-manifest.json."
    case "AstroConfigMissingFontsArray":
      return "Missing `fonts:` array."
    case "AstroConfigMissingManifestReference":
      return `Missing manifest-derived reference: ${issue.reference}`
    case "LayoutMissingFontImport":
      return 'Missing `Font` import from "astro:assets".'
    case "LayoutMissingFontTag":
      return `Missing <Font cssVariable="${issue.cssVariable}" preload />.`
    case "LegacyGoogleFontImport":
      return "Remove fonts.googleapis.com imports from src/styles/global.css."
  }
}

const categoryForIssue = (issue: FontSystemIssue): string => {
  switch (issue._tag) {
    case "MissingFontManifest":
    case "MissingAstroConfig":
    case "MissingLayoutAstro":
    case "MissingGlobalCss":
      return "missing"
    case "InvalidFontManifestJson":
    case "InvalidFontManifestSchema":
      return "manifest"
    case "AstroConfigMissingFontProvidersImport":
    case "AstroConfigMissingManifestImport":
    case "AstroConfigMissingFontsArray":
    case "AstroConfigMissingManifestReference":
      return "astro"
    case "LayoutMissingFontImport":
    case "LayoutMissingFontTag":
      return "layout"
    case "LegacyGoogleFontImport":
      return "css"
  }
}

export const formatFontSystemForAi = (
  result: FontSystemValidationResult,
): string => {
  if (result.isValid) {
    return "Font system is valid. The manifest, Astro config, layout font injection, and global CSS all match the required manifest-based pattern."
  }

  const lines = [
    `Font system validation found ${result.issues.length} ${result.issues.length === 1 ? "issue" : "issues"}.`,
  ]

  const grouped = groupBy(result.issues, categoryForIssue)

  const missing = grouped.get("missing")
  if (missing && missing.length > 0) {
    lines.push("")
    lines.push("Missing required files:")
    for (const issue of missing) {
      lines.push(`  ${formatIssue(issue)}`)
    }
  }

  const manifest = grouped.get("manifest")
  if (manifest && manifest.length > 0) {
    lines.push("")
    lines.push("Invalid font manifest:")
    for (const issue of manifest) {
      lines.push(`  ${formatIssue(issue)}`)
    }
  }

  const astro = grouped.get("astro")
  if (astro && astro.length > 0) {
    lines.push("")
    lines.push("astro.config.mjs issues:")
    for (const issue of astro) {
      lines.push(`  ${formatIssue(issue)}`)
    }
  }

  const layout = grouped.get("layout")
  if (layout && layout.length > 0) {
    lines.push("")
    lines.push("Layout integration issues:")
    for (const issue of layout) {
      lines.push(`  ${formatIssue(issue)}`)
    }
  }

  const css = grouped.get("css")
  if (css && css.length > 0) {
    lines.push("")
    lines.push("Global CSS issues:")
    for (const issue of css) {
      lines.push(`  ${formatIssue(issue)}`)
    }
  }

  return lines.join("\n")
}
